/**
 * Telling the parent how tall we are. RETIRE-WITH-PRICECALCULATOR.
 *
 * The host page frames us with `scrolling="no"` and a `min-height`, and that is
 * the right choice rather than an oversight: a scrollbar inside an iframe fights
 * the page's own scroll and is close to unusable on a phone. But it means the
 * `scroll` message is the ONLY thing standing between a result list and being
 * clipped, with nothing on screen to suggest anything is missing. There is no
 * `ResizeObserver` on the parent to fall back on.
 *
 * So two rules:
 *
 *   MEASURE CONTENT, NOT VIEWPORT. `innerHeight` is the frame the parent gave
 *   us, so reporting it says "I am as tall as you already think I am" and the
 *   height never grows. `documentElement.scrollHeight` is the content.
 *
 *   REPORT ON EVERY LAYOUT CHANGE, not on first paint. Results appearing, a
 *   filter panel opening, a late web font, an orientation change. Cheap to send
 *   and invisible when wrong.
 */

import { clampScroll } from './protocol.js';

export interface Size { width: number; height: number; }

/**
 * The full content box.
 *
 * The max of four numbers rather than one, because which of them is the real
 * height depends on whether the body has margins, whether anything is absolutely
 * positioned and whether a float is uncleared — all of which a park's stylesheet
 * may do to us. Taking the largest over-reports by a margin at worst; taking
 * `scrollHeight` alone under-reports and cuts content off.
 */
export function measure(doc: Document): Size {
  const html = doc.documentElement;
  const body = doc.body;
  return {
    width: Math.max(
      html?.scrollWidth ?? 0, html?.offsetWidth ?? 0,
      body?.scrollWidth ?? 0, body?.offsetWidth ?? 0,
    ),
    height: Math.max(
      html?.scrollHeight ?? 0, html?.offsetHeight ?? 0,
      body?.scrollHeight ?? 0, body?.offsetHeight ?? 0,
      wantedHeight(doc),
    ),
  };
}

/**
 * How tall an open overlay says it needs the document to be.
 *
 * WHY THE ELEMENT IS ASKED RATHER THAN MEASURED. The calendar panel caps itself
 * to the frame — it has to, because `scrolling="no"` means a panel taller than
 * the frame is cut off rather than scrolled to. But a capped panel measures as
 * exactly the cap: its overflow is inside its own month list, so no document
 * metric above can see the height it would have LIKED. Measuring harder would
 * report the cap back, the frame would never grow, and the cap would never
 * lift.
 *
 * So the panel publishes the number instead, in document space, as
 * `data-pw-grow`. `fitPanel` in the guest app writes it; this is the only
 * reader. The contract is one attribute holding one integer, which is about as
 * small as a seam between two apps gets.
 */
function wantedHeight(doc: Document): number {
  let want = 0;
  const nodes = doc.querySelectorAll?.('[data-pw-grow]');
  if (!nodes) return 0;
  for (let i = 0; i < nodes.length; i += 1) {
    const value = Number(nodes[i].getAttribute('data-pw-grow'));
    if (Number.isFinite(value) && value > want) want = value;
  }
  return want;
}

export interface HeightReporter {
  /** Measure and send, unless the numbers are the ones already sent. */
  report(): void;
  stop(): void;
}

/**
 * Measuring, clamping, deduplicating and sending, in one place.
 *
 * One place because BOTH embeds need it and only one of them has a renderer: the
 * tariff card draws its own tiles, so it has no `postMessageResults` to hang an
 * effect off, and an unsized tariff card is clipped at the host's 600px exactly
 * like a clipped result list would be.
 *
 * Deduplicated because a ResizeObserver, a React render and a font load all fire
 * for one visual change, and three identical cross-origin posts is noise in
 * anybody's debugging.
 */
export function createHeightReporter(
  send: (target: string, command: string, argument?: string) => void,
  getDoc: () => Document = () => document,
): HeightReporter {
  let last: string | null = null;
  let stopped = false;

  const report = () => {
    if (stopped) return;
    const doc = getDoc();
    const { width, height } = measure(doc);
    const [w, h] = clampScroll(width, height);
    letOverflowShow(doc);
    const argument = `${w},${h}`;
    if (argument === last) return;
    last = argument;
    send('all', 'scroll', argument);
  };

  const unwatch = watchHeight({ doc: getDoc(), onChange: report });
  report();

  return {
    report,
    stop() { stopped = true; unwatch(); },
  };
}

export interface HeightWatchOptions {
  doc?: Document;
  /** Called whenever anything that could change our height has happened. */
  onChange: () => void;
}

/**
 * Everything that changes our height without React re-rendering.
 *
 * A render-time measurement catches results arriving; these catch the rest. All
 * of them are optional at runtime because the widget also runs under jsdom,
 * where `ResizeObserver` and `document.fonts` may not exist.
 */
export function watchHeight(options: HeightWatchOptions): () => void {
  const doc = options.doc ?? document;
  const win = doc.defaultView;
  const undo: Array<() => void> = [];

  if (win && typeof win.ResizeObserver === 'function') {
    const ro = new win.ResizeObserver(() => options.onChange());
    if (doc.documentElement) ro.observe(doc.documentElement);
    if (doc.body) ro.observe(doc.body);
    undo.push(() => ro.disconnect());
  }
  if (win) {
    const onResize = () => options.onChange();
    win.addEventListener('resize', onResize);
    win.addEventListener('orientationchange', onResize);
    undo.push(() => {
      win.removeEventListener('resize', onResize);
      win.removeEventListener('orientationchange', onResize);
    });
  }
  /*
    Everything a ResizeObserver cannot see.

    A ResizeObserver watches BORDER BOXES. Opening the desktop calendar changes
    no border box anywhere: the popover is `position: absolute`, so `body` keeps
    its height and `documentElement` keeps the viewport's, while the height we
    actually need jumps by four hundred pixels. Same for the panel's own
    `data-pw-grow`, which is an attribute and moves nothing at all.

    So mutations too, coalesced into one measurement per frame — a page turn is
    a burst of them and each would otherwise cost a cross-origin post.
    `report()` drops a repeat anyway, which is what stops `letOverflowShow`'s
    own writes to `body.style` from feeding back in here for ever.
  */
  if (win && typeof win.MutationObserver === 'function' && doc.body) {
    let queued = 0;
    const soon = () => {
      if (queued) return;
      const later = () => { queued = 0; options.onChange(); };
      queued = typeof win.requestAnimationFrame === 'function'
        ? win.requestAnimationFrame(later) : win.setTimeout(later, 16);
    };
    const mo = new win.MutationObserver(soon);
    mo.observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'data-pw-grow'],
    });
    undo.push(() => mo.disconnect());
  }

  // A font that loads after first paint changes every line height at once, and
  // is the classic cause of a footer sliced off the bottom of an iframe.
  const fonts = (doc as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
  if (fonts?.ready) void fonts.ready.then(() => options.onChange()).catch(() => {});

  return () => { for (const fn of undo) fn(); };
}


/**
 * Scroll inside the frame whenever the frame is smaller than what is in it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT ABOUT THE CEILING
 * ---------------------------------------------------------------------------
 *
 * It was, and that was too narrow. The first version enabled scrolling only
 * when the measurement exceeded `SCROLL_MAX`, on the reasoning that a parent
 * which had been told our height would act on it. That reasoning holds for the
 * search embed, whose host page loads a loader; it is simply false for the
 * TARIFF CARD.
 *
 * `/kampeer-tarieven-2026` carries one `<iframe>` and NO SCRIPT AT ALL. Nobody
 * is listening. The `scroll` message goes to a window with no handler, the
 * frame stays at the host's `min-height: 600px` for ever, and a card of any
 * greater height is cut off with `scrolling="no"` to make sure the visitor
 * cannot get at it. That page has looked like that from the day it was framed,
 * and no clamp was involved.
 *
 * So the question to ask is not "did we exceed what the protocol can express"
 * but "is our content taller than our own viewport, right now" — which is true
 * when nobody is listening, when a parent ignored us, and when the value was
 * clamped, without needing to know which.
 *
 * SELF-CORRECTING, which is what makes it safe to apply everywhere. A parent
 * that does resize us grows `clientHeight` to fit, the next measurement finds
 * content and viewport equal, and the scrollbar goes away by itself. The
 * hidden overflow the embed page asks for is therefore still what a visitor
 * gets in every case where the frame is honest — and a scrollbar inside an
 * iframe is poor on a phone, but it is not as poor as a price and a
 * Reserveren button that cannot be reached.
 *
 * ---------------------------------------------------------------------------
 * WHY `body` FOR THE CONTENT AND `documentElement` FOR THE VIEWPORT
 * ---------------------------------------------------------------------------
 *
 * `documentElement.scrollHeight` returns the VIEWPORT when the content is
 * shorter than it — measured in a real browser: a 590px card in a 718px frame
 * reports 718. So it cannot answer "is the content taller"; it is the wrong
 * end of the comparison. `body` is not stretched to the viewport, so its
 * scroll and offset heights are the content, and `documentElement.clientHeight`
 * is exactly the frame we have been given.
 */
function letOverflowShow(doc: Document): void {
  const html = doc.documentElement as HTMLElement | null;
  const body = doc.body as HTMLElement | null;
  if (!html?.style) return;

  const content = Math.max(body?.scrollHeight ?? 0, body?.offsetHeight ?? 0);
  const viewport = html.clientHeight ?? 0;
  // A pixel of tolerance: sub-pixel layout rounds, and a scrollbar that
  // appears over half a pixel of overflow is a flicker nobody can explain.
  const clipped = viewport > 0 && content > viewport + 1;

  // `''` and not `'hidden'`: the stylesheet already says hidden, and writing it
  // back as an inline style would outrank a future change to that stylesheet.
  //
  // Written only when it DIFFERS. `body` is under the MutationObserver above,
  // and a browser records an attribute write whether or not the value moved —
  // so an unconditional assignment here would wake the observer, which would
  // measure, which would assign again, once a frame for ever.
  //
  // `html` ONLY. `body` is as tall as its in-flow content, so clipping there
  // cuts an absolutely positioned popover at the body's edge rather than at the
  // frame's — see `embedPage.ts`. Scrolling belongs to the viewport.
  const value = clipped ? 'auto' : '';
  if (html.style.overflow !== value) html.style.overflow = value;
}
